"use client";

import { Button, Input } from "@softmax/design-system";
import {
  colors,
  fonts,
  fontSizes,
  spacing,
} from "@softmax/design-system/tokens.stylex";
import * as stylex from "@stylexjs/stylex";
import { useId } from "react";
import { PlayerImagePicker } from "./player-image-picker";

const styles = stylex.create({
  form: { display: "flex", flexDirection: "column", gap: spacing.space12 },
  fields: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: spacing.space10,
  },
  name: { flex: "1 1 180px", width: "100%", maxWidth: 260, minWidth: 0 },
  hidden: {
    position: "absolute",
    width: 1,
    height: 1,
    padding: 0,
    margin: -1,
    overflow: "hidden",
    clip: "rect(0, 0, 0, 0)",
    whiteSpace: "nowrap",
    borderWidth: 0,
  },
  message: {
    margin: 0,
    fontFamily: fonts.sans,
    fontSize: fontSizes.reading,
    lineHeight: "22px",
    color: colors.muted,
  },
  error: { color: colors.danger },
  action: { alignSelf: "flex-start" },
});

type OnboardingPlayerFormProps = Readonly<{
  name: string;
  imageName: string | null;
  previewUrl: string | null;
  pending: boolean;
  error: string | null;
  existingPlayer: boolean;
  onNameChange: (name: string) => void;
  onImageChange: (image: File | null) => void;
  onSubmit: () => void;
}>;

export function OnboardingPlayerForm({
  name,
  imageName,
  previewUrl,
  pending,
  error,
  existingPlayer,
  onNameChange,
  onImageChange,
  onSubmit,
}: OnboardingPlayerFormProps) {
  const id = useId();
  const missingName = name.trim().length === 0;
  const requirementsText = missingName ? "Add a name to continue." : null;
  const requirementsId = requirementsText ? `${id}-requirements` : undefined;

  return (
    <form
      {...stylex.props(styles.form)}
      onSubmit={(event) => {
        event.preventDefault();
        onSubmit();
      }}
    >
      <div {...stylex.props(styles.fields)}>
        <div {...stylex.props(styles.name)}>
          <label {...stylex.props(styles.hidden)} htmlFor={`${id}-name`}>
            Player name
          </label>
          <Input
            id={`${id}-name`}
            name="player-name"
            value={name}
            onChange={(event) => onNameChange(event.target.value)}
            placeholder="Player name"
            autoComplete="off"
            maxLength={64}
            aria-describedby={requirementsId}
          />
        </div>
        <PlayerImagePicker
          id={`${id}-image`}
          imageName={imageName}
          previewUrl={previewUrl}
          requirementsId={requirementsId}
          onImageChange={onImageChange}
        />
      </div>
      {requirementsText ? (
        <p id={requirementsId} {...stylex.props(styles.message)}>
          {requirementsText}
        </p>
      ) : null}
      <div {...stylex.props(styles.action)}>
        <Button
          type="submit"
          variant="primary"
          disabled={missingName || pending}
        >
          {pending ? "Saving…" : existingPlayer ? "Save" : "Create"}
        </Button>
      </div>
      {error ? (
        <output {...stylex.props(styles.message, styles.error)}>{error}</output>
      ) : null}
    </form>
  );
}
